import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useAirline } from '../contexts/AirlineContext';
import type { BasePrivilegeLevel } from '../types/pilot';

/**
 * Licence-holder identity fields (migration 164). All optional — the
 * combobox "quick-add" flow still works with just a name.
 */
export interface OrgPilot {
  id: string;
  org_id: string;
  full_name: string;
  airline_code: string | null;
  email: string | null;
  role_preference: 'captain' | 'first_officer' | 'any';
  linked_user_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;

  // Licence-holder identity (nullable on new ad-hoc pilots)
  licence_id: string | null;
  date_of_birth: string | null;
  nationality: string | null;
  base_privileges: BasePrivilegeLevel | null;
  total_flight_hours: number | null;
  total_pic_hours: number | null;
  reference_date_flthours: string | null;
}

export interface CreatePilotInput {
  full_name: string;
  airline_code?: string;
  email?: string;
  role_preference?: 'captain' | 'first_officer' | 'any';
}

export interface UpdatePilotLicenceIdentityInput {
  licence_id?: string | null;
  date_of_birth?: string | null;
  nationality?: string | null;
  base_privileges?: BasePrivilegeLevel | null;
  total_flight_hours?: number | null;
  total_pic_hours?: number | null;
  reference_date_flthours?: string | null;
}

export function useOrgPilots() {
  const { user } = useAuth();
  const { selectedAirline } = useAirline();

  async function searchPilots(query: string): Promise<OrgPilot[]> {
    if (!selectedAirline?.airline_id || !query.trim()) return [];

    const { data, error } = await supabase
      .from('org_pilots')
      .select('*')
      .eq('org_id', selectedAirline.airline_id)
      .ilike('full_name', `%${query.trim()}%`)
      .order('full_name')
      .limit(20);

    if (error) throw error;
    return (data ?? []) as OrgPilot[];
  }

  async function getPilot(orgPilotId: string): Promise<OrgPilot | null> {
    const { data, error } = await supabase
      .from('org_pilots')
      .select('*')
      .eq('id', orgPilotId)
      .maybeSingle();

    if (error) throw error;
    return (data as OrgPilot | null) ?? null;
  }

  async function createPilot(input: CreatePilotInput): Promise<OrgPilot> {
    if (!selectedAirline?.airline_id) throw new Error('No airline selected');
    if (!user?.id) throw new Error('Not authenticated');

    const normalizedCode = input.airline_code
      ? input.airline_code.replace(/[^A-Za-z0-9]/g, '').slice(0, 7).toUpperCase()
      : null;

    const { data, error } = await supabase
      .from('org_pilots')
      .insert({
        org_id: selectedAirline.airline_id,
        full_name: input.full_name,
        airline_code: normalizedCode,
        email: input.email ?? null,
        role_preference: input.role_preference ?? 'any',
        created_by: user.id,
      })
      .select('*')
      .single();

    if (error) {
      const isDuplicateCode =
        (error as { code?: string }).code === '23505' &&
        ((error.message ?? '').includes('idx_org_pilots_unique_code') ||
          ((error as { details?: string }).details ?? '').includes('idx_org_pilots_unique_code'));

      if (isDuplicateCode && normalizedCode) {
        const { data: existing } = await supabase
          .from('org_pilots')
          .select('full_name')
          .eq('org_id', selectedAirline.airline_id)
          .eq('airline_code', normalizedCode)
          .maybeSingle();

        const existingName = (existing as { full_name?: string } | null)?.full_name;
        throw new Error(
          existingName
            ? `Pilot ID "${normalizedCode}" is already assigned to ${existingName} in this airline. If that's the same person, go back and select them from the list. Otherwise, use a different ID.`
            : `Pilot ID "${normalizedCode}" is already in use in this airline. Please use a different ID.`,
        );
      }

      throw error;
    }
    return data as OrgPilot;
  }

  /**
   * Update licence-holder identity fields on an existing org_pilot.
   * Authorised by RLS: airline admins or (via separate write path) the
   * creating instructor.
   */
  async function updatePilotLicenceIdentity(
    orgPilotId: string,
    input: UpdatePilotLicenceIdentityInput,
  ): Promise<OrgPilot> {
    const { data, error } = await supabase
      .from('org_pilots')
      .update(input)
      .eq('id', orgPilotId)
      .select('*')
      .single();

    if (error) throw error;
    return data as OrgPilot;
  }

  return { searchPilots, getPilot, createPilot, updatePilotLicenceIdentity };
}
